import { useCallback, useEffect, useRef } from "react";

/**
 * Sticky Mobile Offset Registry
 * -----------------------------
 * Components with bottom-fixed bars register their *real measured* height so
 * floating widgets (chatbot FAB, scroll-to-top) sit just above them via the
 * `--ph-sticky-offset` CSS variable.
 *
 * Two APIs:
 *
 *  - `useStickyMobileOffsetRef<T>(enabled?)` → returns a ref. Attach it to
 *    the bar's outer element and the hook tracks its real height through a
 *    ResizeObserver. This is the preferred API: it adapts to content/breakpoint
 *    changes automatically and never gets stale.
 *
 *  - `useStickyMobileOffset(heightPx, enabled?)` → legacy fallback for cases
 *    where measuring is impractical (e.g. the hook is called above the JSX).
 *    Pass a fixed pixel height.
 *
 * Both register into the same registry; the maximum value wins (no double
 * counting when overlapping bars register simultaneously). Only applies under
 * the `lg` breakpoint (<1024px); above that, the offset is forced to 0.
 */
const registry = new Map<string, number>();
let counter = 0;
let mqListenerAttached = false;
let mqInstance: MediaQueryList | null = null;

const getMq = () => {
  if (!mqInstance && typeof window !== "undefined") {
    mqInstance = window.matchMedia("(max-width: 1023px)");
  }
  return mqInstance;
};

const recompute = () => {
  if (typeof document === "undefined") return;
  const mq = getMq();
  const isMobile = mq?.matches ?? false;
  if (!isMobile || registry.size === 0) {
    document.documentElement.style.setProperty("--ph-sticky-offset", "0px");
    return;
  }
  const max = Math.max(0, ...Array.from(registry.values()));
  document.documentElement.style.setProperty("--ph-sticky-offset", `${max}px`);
};

const ensureMqListener = () => {
  if (mqListenerAttached) return;
  const mq = getMq();
  if (!mq) return;
  mq.addEventListener("change", recompute);
  mqListenerAttached = true;
};

const setEntry = (id: string, heightPx: number) => {
  // Round and clamp to avoid sub-pixel thrash and absurd values
  const safe = Math.max(0, Math.min(400, Math.round(heightPx)));
  const prev = registry.get(id);
  if (prev === safe) return;
  registry.set(id, safe);
  recompute();
};

const removeEntry = (id: string) => {
  if (!registry.delete(id)) return;
  recompute();
};

/**
 * Legacy / fixed-height registration. Prefer `useStickyMobileOffsetRef`.
 */
export const useStickyMobileOffset = (heightPx: number, enabled = true) => {
  useEffect(() => {
    if (!enabled) return;
    const id = `sticky-fixed-${++counter}`;
    ensureMqListener();
    setEntry(id, heightPx);
    return () => removeEntry(id);
  }, [heightPx, enabled]);
};

/**
 * Measure-driven registration. Attach the returned ref to the bar's outer
 * element. Height is observed via ResizeObserver and re-registered on change.
 */
export const useStickyMobileOffsetRef = <T extends HTMLElement = HTMLDivElement>(
  enabled = true
) => {
  const ref = useRef<T | null>(null);
  const idRef = useRef<string | null>(null);

  const setRef = useCallback(
    (node: T | null) => {
      ref.current = node;
    },
    []
  );

  useEffect(() => {
    if (!enabled) {
      if (idRef.current) {
        removeEntry(idRef.current);
        idRef.current = null;
      }
      return;
    }
    const node = ref.current;
    if (!node) return;

    ensureMqListener();
    const id = `sticky-measured-${++counter}`;
    idRef.current = id;

    const measure = () => {
      const rect = node.getBoundingClientRect();
      setEntry(id, rect.height);
    };
    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(node);

    return () => {
      ro.disconnect();
      removeEntry(id);
      idRef.current = null;
    };
    // We intentionally re-run only on enabled toggles; the ref callback handles
    // node identity changes via the closure above.
  }, [enabled]);

  return setRef;
};

export default useStickyMobileOffset;
